import { useSelector } from "react-redux";
import type { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

const shimmer =
  "bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse";

interface TicketTableListingShimmerProps {
  rowCount?: number;
  stageCount?: number;
}

const ShimmerRow = ({ index }: { index: number }) => (
  <tr
    className={`${
      index < 9 ? "border-b border-gray-75 dark:border-gray-75-dk" : ""
    }`}
  >
    {/* S. No. */}
    <td className="p-0! h-[44px]!" style={{ width: 60 }}>
      <div className="px-3">
        <div className={`h-3.5 w-5 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Ticket ID */}
    <td className="p-0! h-[44px]!" style={{ width: 170 }}>
      <div className="px-3">
        <div className={`h-3.5 w-24 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Ticket Category */}
    <td className="p-0! h-[44px]!" style={{ width: 155 }}>
      <div className="px-3">
        <div className={`h-5 w-20 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Ticket Subject */}
    <td className="p-0! h-[44px]!" style={{ width: 280 }}>
      <div className="px-3">
        <div className={`h-3.5 w-48 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Priority */}
    <td className="p-0! h-[44px]!" style={{ width: 105 }}>
      <div className="px-3 flex items-center gap-1">
        <div className={`h-3.5 w-3.5 rounded shrink-0 ${shimmer}`} />
        <div className={`h-3.5 w-12 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Ticket Status */}
    <td className="p-0! h-[44px]!" style={{ width: 140 }}>
      <div className="px-3">
        <div className={`h-6 w-24 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Client Code */}
    <td className="p-0! h-[44px]!" style={{ width: 135 }}>
      <div className="px-3 flex items-center gap-1.5">
        <div className={`h-3 w-3 rounded shrink-0 ${shimmer}`} />
        <div className={`h-3.5 w-16 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Tags */}
    <td className="p-0! h-[44px]!" style={{ width: 210 }}>
      <div className="px-3 flex items-center gap-1.5">
        <div className={`h-5 w-20 rounded ${shimmer}`} />
        <div className={`h-5 w-16 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Ticket Owner */}
    <td className="p-0! h-[44px]!" style={{ width: 155 }}>
      <div className="px-3 flex items-center gap-1.5">
        <div className={`h-[22px] w-[22px] rounded-full shrink-0 ${shimmer}`} />
        <div className={`h-3.5 w-20 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Last Activity */}
    <td className="p-0! h-[44px]!" style={{ width: 165 }}>
      <div className="px-3">
        <div className={`h-3.5 w-28 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Created */}
    <td className="p-0! h-[44px]!" style={{ width: 185 }}>
      <div className="px-3">
        <div className={`h-3.5 w-32 rounded ${shimmer}`} />
      </div>
    </td>
    {/* Property Name */}
    <td className="p-0! h-[44px]!" style={{ width: 130 }}>
      <div className="px-3">
        <div className={`h-3.5 w-20 rounded ${shimmer}`} />
      </div>
    </td>
  </tr>
);

const getHeaderColumns = (region?: string) => {
  const labels = getHelpdeskLabels(region);
  return [
    { label: "S. No.", width: 60 },
    { label: labels.ticketId, width: 170 },
    { label: labels.ticketCategory, width: 155 },
    { label: labels.ticketSubject, width: 280 },
    { label: "Priority", width: 105 },
    { label: `${labels.ticketSingular} Status`, width: 140 },
    { label: "Client Code", width: 135 },
    { label: "Tags", width: 210 },
    { label: `${labels.ticketSingular} Owner`, width: 155 },
    { label: "Last Activity", width: 165 },
    { label: "Created", width: 185 },
    { label: "Property Name", width: 130 },
  ];
};

const StageShimmerBlock = ({ rowCount, headerColumns }: { rowCount: number; headerColumns: ReturnType<typeof getHeaderColumns> }) => (
  <div className="mb-4">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-4 h-4 rounded ${shimmer}`} />
      <div className={`h-[30px] w-[120px] rounded-lg ${shimmer}`} />
    </div>

    <div className="w-full border border-border-gray dark:border-border-gray-dk rounded-[8px]">
      <div className="flex rounded-[8px]">
        <div className="flex-1 overflow-auto">
          <table className="border-collapse min-w-full">
            <colgroup>
              {headerColumns?.map((col, i) => (
                <col key={i} style={{ width: col?.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr>
                {headerColumns?.map((col, i) => (
                  <th
                    key={i}
                    className={`text-md font-medium text-gray-375 dark:text-gray-375-dk bg-table-header dark:bg-table-header-dk border-b border-gray-75 dark:border-gray-75-dk whitespace-nowrap ${
                      i === 0 ? "rounded-tl-[6px]" : ""
                    } ${i === headerColumns?.length - 1 ? "rounded-tr-[6px]" : ""}`}
                    style={{ width: col?.width }}
                  >
                    <div className="text-left px-3 py-2">
                      <div className={`h-3.5 rounded ${shimmer}`} style={{ width: `${Math.min(col?.label?.length * 7, col?.width - 24)}px` }} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount })?.map((_, i) => (
                <ShimmerRow key={i} index={i} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

export const TicketTableListingShimmer = ({
  rowCount = 10,
  stageCount = 3,
}: TicketTableListingShimmerProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const headerColumns = getHeaderColumns(region);

  return (
    <>
      {Array.from({ length: stageCount })?.map((_, i) => (
        <StageShimmerBlock key={i} rowCount={rowCount} headerColumns={headerColumns} />
      ))}
    </>
  );
};
